/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useDispatch, useSelector } from 'react-redux';
import {
  selectTrendSymmetricComparisonToggle,
  selectTrendTimeDateComparison,
  selectTrendTimeAlignmentToggle,
  selectTrendSegmentChartBreakdown,
  selectIsSegmentDetailed,
  selectTrendSegmentFocusOn,
  selectIsForecast,
  selectTrendStartDate,
  selectTrendEndDate,
  selectIsCumulative,
  selectMovingAverage,
  selectTrendTimeWeeklyView,
  selectIsMovingAverage,
  selectIsPercentageChange,
  selectTrendTimeStartDate,
  selectTrendTimeEndDate,
  selectTrendGranularity,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import {
  selectHotelName,
  selectLastPartialBookingDate,
  selectLocalCurrency,
  selectPartialHours,
  selectPmsSync,
  selectResetBookingDate,
  selectTrendBookingDate,
  selectVisualFilters,
} from 'modules/dashboard/selectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/system';
import MuiTabular from 'modules/common/components/mui-tabular';
import { IconButton, Typography } from '@mui/material';
import {
  selectAuthUser,
  selectOrganizationId,
  selectSessionId,
} from 'modules/common/auth/selectors';
import { AssistantIcon } from 'modules/common/components';
import { EXPORT_BUTTON_STYLE } from 'modules/common/components/mui-tabular/style';
import { EXPORT_OPTIONS } from 'modules/dashboard/constants';
import DownloadIcon from '@mui/icons-material/Download';
import { useDebouncedCallback } from 'use-debounce';
import {
  TREND_CUMULATIVE_FORECAST_TABLE_COLUMNS,
  TREND_CUMULATIVE_TABLE_COLUMNS,
  TREND_MOVING_AVERAGE_TABLE_COLUMNS,
  TREND_PERCENTAGE_TABLE_COLUMNS,
  TREND_TABLE_COLUMNS,
  TREND_WEEKLY_FORECAST_TABLE_COLUMNS,
  TREND_WEEKLY_TABLE_COLUMNS,
} from '../../../constants';
import {
  buildTrendFilters,
  buildTrendHeaderData,
  getColumns,
  handleCeruleanLogging,
} from '../../../function';
import TabularFilterHeader from './filter-header';
import { selectTrendData, selectTrendDataResponseId } from '../../../selectors';
import { trendTabularActions } from '../../../slice';


/**
 * Trend Time tabular visual report with filters
 */

const MuiTrendTimeTabularWidget = ({ setIsTimeLoaded, isLoaded }) => {
  const bookingDate = useSelector(selectTrendBookingDate);
  const currencyType = useSelector(selectLocalCurrency);
  const hotels = useSelector(selectHotelName);
  const targetStartDate = useSelector(selectTrendStartDate);
  const targetEndDate = useSelector(selectTrendEndDate);
  const alignmentToggle = useSelector(selectTrendTimeAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectTrendSymmetricComparisonToggle);
  const dateComparison = useSelector(selectTrendTimeDateComparison);
  const pms = useSelector(selectPmsSync);
  const visualFilters = useSelector(selectVisualFilters);
  const chartBreakdown = useSelector(selectTrendSegmentChartBreakdown);
  const isDetailed = useSelector(selectIsSegmentDetailed);
  const focusOn = useSelector(selectTrendSegmentFocusOn);
  const isForecast = useSelector(selectIsForecast);
  const isCumulative = useSelector(selectIsCumulative);
  const organizationId = useSelector(selectOrganizationId);
  const movingAverage = useSelector(selectMovingAverage);
  const isMovingAverage = useSelector(selectIsMovingAverage);
  const isWeeklyView = useSelector(selectTrendTimeWeeklyView);
  const isPercentageChange = useSelector(selectIsPercentageChange);
  const endDate = useSelector(selectTrendTimeEndDate);
  const startDate = useSelector(selectTrendTimeStartDate);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const trendData = useSelector(selectTrendData);
  const trendDataResponseId = useSelector(selectTrendDataResponseId);
  const trendGranularity = useSelector(selectTrendGranularity);
  const partialHours = useSelector(selectPartialHours);
  const lastBookingDate = useSelector(selectLastPartialBookingDate);
  const dispatch = useDispatch();
  //
  const tableRef = useRef();
  //
  const [exportType, setExportType] = useState(EXPORT_OPTIONS.DATA_EXPORT);
  //
  const [isProcessing, setIsProcessing] = useState(false);
  //
  const [exportData, setExportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  // Debounce the request to avoid excessive calls
  const fetchData = useDebouncedCallback(async () => {
    // Build the filters for the API request
    const filters = buildTrendFilters({
      bookingDate,
      targetStartDate,
      targetEndDate,
      alignmentToggle,
      symmetricComparisonToggle,
      dateComparison,
      currencyType,
      hotels,
      pms,
      chartBreakdown,
      visualFilters,
      isDetailed,
      focusOn,
      isForecast,
      movingAverage,
      isMovingAverage,
      startDate,
      endDate,
      resetBookingDate,
      trendGranularity,
      partialHours,
      lastBookingDate,
    });
    //
    const columns = getColumns({
      isPercentageChange,
      isMovingAverage,
      isCumulative,
      isWeeklyView,
      isForecast,
    });
    //
    let start;
    try {
      setIsTimeLoaded(false);
      // Log the request time for performance monitoring
      handleCeruleanLogging({
        message: `Trend Time Tabular Filters: ${JSON.stringify(filters)}`,
        sessionId,
        user,
        eventType: 'Cerulean Filters',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request start: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loading',
      });
      start = performance.now();
      //
      setExportData({
        columns,
        filters,
        title: 'mui-tabular-trend-time',
      });
      dispatch(
        trendTabularActions.getTrendData({
          organizationId,
          columns,
          filters,
        })
      );
    } catch (error) {
      handleCeruleanLogging({
        message: `Error fetching trend time tabular data: ${error}`,
        sessionId,
        user,
        eventType: 'Cerulean Error',
      });
    } finally {
      // Log the time taken for the request
      const end = performance.now();
      handleCeruleanLogging({
        message: `Trend Tabular Request finished: ${new Date()}`,
        sessionId,
        user,
        eventType: 'Cerulean Loaded',
      });
      handleCeruleanLogging({
        message: `Trend Tabular Request time: ${((end - start) / 1000).toFixed(2)} seconds`,
        sessionId,
        user,
        eventType: 'Cerulean Request Time',
      });
    }
  }, 500);
  //
  useEffect(() => {
    // loaded when trend data is available or a request is completed, also after an error
    if (trendData || trendDataResponseId > 0) {
      setIsTimeLoaded(true);
    }
  }, [trendData, trendDataResponseId]);
  //
  useEffect(() => {
    fetchData();
  }, [
    bookingDate,
    currencyType,
    hotels,
    alignmentToggle,
    symmetricComparisonToggle,
    dateComparison,
    setIsTimeLoaded,
    targetStartDate,
    targetEndDate,
    pms,
    focusOn,
    isCumulative,
    isForecast,
    movingAverage,
    isWeeklyView,
    isMovingAverage,
    isPercentageChange,
    startDate,
    endDate,
    trendGranularity,
    partialHours,
    lastBookingDate,
  ]);
  //
  const headerData = useMemo(
    () =>
      buildTrendHeaderData({
        hotelName: hotels?.label,
        bookingDate,
        targetStartDate,
        targetEndDate,
        alignmentToggle,
        symmetricComparisonToggle,
        currencyType,
        aligned: alignmentToggle,
        pmsSync: pms,
        dateComparison,
        startDate,
        endDate,
        isCumulative,
        isPercentageChange,
        isWeeklyView,
        isMovingAverage,
        resetBookingDate,
        partialHours,
        lastBookingDate,
      }),
    [
      hotels,
      bookingDate,
      targetEndDate,
      targetStartDate,
      alignmentToggle,
      startDate,
      endDate,
      symmetricComparisonToggle,
      currencyType,
      pms,
      dateComparison,
      isCumulative,
      isPercentageChange,
      isWeeklyView,
      isMovingAverage,
      resetBookingDate,
      trendGranularity,
      partialHours,
      lastBookingDate,
    ]
  );
  const tableSchema = () => {
    if (isCumulative && isForecast) {
      return TREND_CUMULATIVE_FORECAST_TABLE_COLUMNS;
    }
    if (isWeeklyView && isForecast) {
      return TREND_WEEKLY_FORECAST_TABLE_COLUMNS;
    }
    if (isMovingAverage && isPercentageChange) {
      return TREND_PERCENTAGE_TABLE_COLUMNS;
    }
    if (isCumulative) {
      return TREND_CUMULATIVE_TABLE_COLUMNS;
    }
    if (isWeeklyView) {
      return TREND_WEEKLY_TABLE_COLUMNS;
    }
    if (isMovingAverage) {
      return TREND_MOVING_AVERAGE_TABLE_COLUMNS;
    }
    return TREND_TABLE_COLUMNS;
  };
  //
  return (
    <Box px={10}>
      {!trendData ? (
        <Typography fontWeight={500}>Loading Data...</Typography>
      ) : (
        <Box>
          <Box display="flex" justifyContent="end">
            <IconButton
              size="medium"
              onClick={() => setIsProcessing(true)}
              sx={EXPORT_BUTTON_STYLE}
            >
              <Typography
                sx={{
                  mr: 1,
                }}
              >
                Export Data
              </Typography>
              <DownloadIcon />
            </IconButton>
          </Box>
          <MuiTabular
            ref={tableRef}
            columns={tableSchema()}
            isProcessing={isProcessing}
            isLoadingData={!isLoaded}
            setIsProcessing={setIsProcessing}
            isAggregate={!(isMovingAverage || isPercentageChange)}
            headerComponent={
              <TabularFilterHeader
                title={headerData?.title}
                hotelName={headerData?.hotelName}
                isHotelSet={Boolean(hotels?.hotels)}
                sections={headerData?.sections}
                interpreterFragment={
                  <AssistantIcon
                    exportData={exportData}
                    visualName={headerData?.title}
                    isProcessing={isExporting}
                    setIsProcessing={setIsExporting}
                    disabled={isProcessing || !isLoaded || isExporting}
                    isCerulean
                    getExcelExportData={tableRef?.current?.getExcelData}
                    exportType={exportType}
                    setExportType={setExportType}
                  />
                }
              />
            }
            data={trendData}
            headerFiltersInfo={headerData}
            customBackgroundStyle={{ paddingLeft: 3, maxWidth: { md: '72vw', lg: '76vw' } }}
            fileName="TVT_Daily"
            primaryTextColor={isPercentageChange ? '#000' : undefined}
            secondaryTextColor={isPercentageChange ? '#000' : undefined}
          />
        </Box>
      )}
    </Box>
  );
};
//
export default MuiTrendTimeTabularWidget;
